import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  CrossIcon,
  File04Icon,
  FolderIcon,
  UploadCloud02Icon,
} from 'assets/icons';
import { ReactComponent as DropboxLogo } from 'assets/images/source-dropbox.svg';
import { ReactComponent as GoogleDriveLogo } from 'assets/images/source-google-drive.svg';
import { ReactComponent as IcloudLogo } from 'assets/images/source-icloud.svg';
import { FolderInput } from 'common/import-source-tiles';
import { keepPickedFolder } from 'helpers/picked-folder';
import {
  describeSelection,
  FolderHandling,
  PickedItem,
  pickedFromInput,
  readDrop,
  SelectionShape,
  toImportFiles,
} from 'helpers/upload-selection';
import { classnames } from 'helpers/utils';
import { goToPage, RouteName } from 'routes';
import { FolderQuestion } from './folder-question';
import styles from './upload-modal.module.scss';

interface UploadModalProps {
  vaultId: number;
  // Opened from an album: files go into it, and the review returns there.
  album?: { id: number; name: string; thumbnailUrl?: string | null };
  onClose: () => void;
}

// The import page's `?source=` keys for the cloud sources.
const CLOUD_SOURCES = [
  {
    key: 'drive',
    name: 'Google Drive',
    logo: <GoogleDriveLogo className={styles.driveLogo} />,
    logoClass: styles.sourceLogoDrive,
  },
  {
    key: 'dropbox',
    name: 'Dropbox',
    logo: <DropboxLogo />,
    logoClass: '',
  },
  {
    key: 'icloud',
    name: 'iCloud',
    logo: <IcloudLogo />,
    logoClass: styles.sourceLogoIcloud,
  },
];

// "Upload": one place to add files and folders. Whatever is picked or dropped
// goes to the import review, which files it into albums (one per folder).
// Cloud sources keep their own steps on the import page.
export const UploadModal = ({ vaultId, album, onClose }: UploadModalProps) => {
  const albumId = album?.id;
  // Folders with subfolders (or several folders, from an album) wait here
  // while the user says what they should become.
  const [question, setQuestion] = useState<{
    items: PickedItem[];
    shape: SelectionShape;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const proceed = (
    items: PickedItem[],
    shape: SelectionShape,
    handling: FolderHandling,
  ) => {
    // The review names the folder a pick came from ("from: …").
    const pick = keepPickedFolder(
      toImportFiles(items, handling),
      shape.pickedFolder?.name ?? null,
    );
    onClose();
    goToPage(
      RouteName.VaultImport,
      { id: vaultId },
      { source: 'computer', pick, ...(albumId ? { album: albumId } : {}) },
    );
  };

  // Files, and folders without subfolders, go straight to the review. The
  // question is only about what subfolders (or, in an album, several folders)
  // should become.
  const review = (items: PickedItem[]) => {
    if (items.length === 0) return;
    const shape = describeSelection(items);
    const hasFolders = shape.groups.length > 0;
    const ask = album
      ? hasFolders && (!!shape.sourceFolder || shape.groups.length > 1)
      : !!shape.sourceFolder;
    if (ask) setQuestion({ items, shape });
    // A plain folder added from an album: its files go into that album.
    else proceed(items, shape, album && hasFolders ? 'loose' : 'albums');
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    setReading(true);
    try {
      review(await readDrop(event.dataTransfer));
    } finally {
      setReading(false);
    }
  };

  const importFromCloud = (source: string) => {
    onClose();
    goToPage(
      RouteName.VaultImport,
      { id: vaultId },
      { source, ...(albumId ? { album: albumId } : {}) },
    );
  };

  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
      >
        {question && (
          <FolderQuestion
            shape={question.shape}
            album={album}
            onBack={() => setQuestion(null)}
            onClose={onClose}
            onContinue={(handling) =>
              proceed(question.items, question.shape, handling)
            }
          />
        )}

        <div
          className={classnames(styles.picker, {
            [styles.pickerHidden]: !!question,
          })}
        >
          <div className={styles.header}>
            <span className={styles.featuredIcon}>
              <UploadCloud02Icon />
            </span>
            <h2 id="upload-modal-title" className={styles.title}>
              Upload
            </h2>
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close"
            >
              <CrossIcon />
            </button>
          </div>

          <div className={styles.body}>
            <div
              className={classnames(styles.dropZone, {
                [styles.dropZoneActive]: dragging,
              })}
              role="presentation"
              // "Click to upload": anywhere in the zone opens the file
              // dialog; the two buttons open their own.
              onClick={(e) => {
                if (reading || (e.target as HTMLElement).closest('button')) {
                  return;
                }
                fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                setDragging(true);
              }}
              onDragLeave={(e) => {
                // Leaving for a child of the zone isn't leaving the zone.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDragging(false);
                }
              }}
              onDrop={handleDrop}
            >
              <span className={styles.dropIcon}>
                <UploadCloud02Icon />
              </span>
              <span className={styles.dropText}>
                {reading
                  ? 'Reading your files…'
                  : 'Drag and drop or click to upload'}
              </span>
              <div className={styles.chooseButtons}>
                <button
                  type="button"
                  className={styles.chooseButton}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={reading}
                >
                  <File04Icon />
                  <span>Browse Files</span>
                </button>
                <button
                  type="button"
                  className={styles.chooseButton}
                  onClick={() => folderInputRef.current?.click()}
                  disabled={reading}
                >
                  <FolderIcon />
                  <span>Browse Folders</span>
                </button>
              </div>
            </div>

            <div className={styles.cloud}>
              <span className={styles.cloudLabel}>Or import from:</span>
              <div className={styles.sources}>
                {CLOUD_SOURCES.map((source) => (
                  <button
                    key={source.key}
                    type="button"
                    className={styles.source}
                    onClick={() => importFromCloud(source.key)}
                  >
                    <span
                      className={classnames(
                        styles.sourceLogo,
                        source.logoClass,
                      )}
                      aria-hidden
                    >
                      {source.logo}
                    </span>
                    <span className={styles.sourceName}>{source.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            // Clear so picking the same files again still fires onChange.
            e.target.value = '';
            review(pickedFromInput(files));
          }}
        />
        <FolderInput
          inputRef={folderInputRef}
          onPick={(files) => review(pickedFromInput(files))}
        />
      </div>
    </div>,
    document.getElementById('modal') as HTMLElement,
  );
};
